import { sin, TAU } from '../sim/math';
import type { InputFrame } from '../sim/World';

/* ═══════════════════════════════════════════════════════════════════════════
   L'attract mode — les ENTRÉES de la démonstration.

   Après douze secondes d'inactivité sur l'écran-titre, une partie de démo
   tourne en boucle derrière le menu à 25 % d'opacité (PLAN.md §8, J14). Ce
   n'est pas un enregistrement binaire mais une FONCTION du numéro de tick :
   même effet vitrine, coût de production nul, rien à charger, et surtout —
   c'est le MEILLEUR test de déterminisme du projet. Rejouée mille fois à partir
   de la même graine, la boucle doit rester identique au bit près ; le jour où
   elle dérive, c'est qu'un `Math.sin` s'est glissé quelque part.

   Deux exigences se rejoignent ici :

   1. Les entrées passent par les TABLES trigonométriques committées (`sim/math`),
      jamais `Math.sin` — sinon la démo divergerait entre navigateurs et la
      boucle ne se refermerait pas. La règle ESLint de `content/` l'impose déjà.

   2. Le mouvement doit RESSEMBLER à un joueur, pas à un métronome : un tissage
      de deux sinusoïdes de périodes incommensurables ne se répète pas à l'œil
      sur la durée de la boucle, là où une seule fréquence trahirait la machine.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Graine fixe du monde de démonstration. Volontairement indépendante de la
 *  sauvegarde : l'attract mode doit produire EXACTEMENT la même partie chez tout
 *  le monde, c'est ce qui en fait un test de déterminisme. */
export const ATTRACT_SEED = 0x0a11ac7;

/** Durée d'une boucle de démonstration, en ticks (30 s à 120 Hz). */
export const ATTRACT_TICKS = 3600;

// Périodes du tissage, en ticks. Choisies premières entre elles pour que le
// motif combiné ne se referme pas avant la fin de la boucle.
const WX1 = TAU / 190;
const WX2 = TAU / 331;
const WY = TAU / 257;

/** Entrées SCRIPTÉES du vaisseau de démonstration au tick donné.
 *
 *  Écrit dans `out` et le renvoie — même contrat que le générateur du golden :
 *  une seule `InputFrame` recyclée, aucune allocation par tick. `moveX/moveY`
 *  sont des INTENTIONS (−1..1) que `playerSystem` intègre avec inertie puis
 *  borne au champ ; le vaisseau ondule donc dans le tiers bas sans jamais en
 *  sortir, pendant que le tir automatique fauche ce qui descend. */
export function attractInput(tick: number, out: InputFrame): InputFrame {
  const x = sin(tick * WX1) + 0.55 * sin(tick * WX2 + 1.3);
  out.moveX = x < -1 ? -1 : x > 1 ? 1 : x;
  // Oscillation verticale douce et de moyenne nulle : la position revient, elle
  // ne dérive pas vers le haut (l'intention s'intègre autour de l'équilibre).
  out.moveY = 0.34 * sin(tick * WY);
  // Tir en RAFALES disciplinées, jamais la gâchette bloquée : un cycle court sous
  // ~58 % de service reste sous le seuil de chauffe (le tir moyen ne dépasse pas le
  // refroidissement) → la démo tire sans jamais surchauffer ni s'enrayer, et montre
  // au passage le bon geste. Repli modulaire à la main (pas de `%`, garde ESLint de
  // `content/` : le déterminisme des tables).
  out.fire = tick - Math.floor(tick / 30) * 30 < 18;
  // Focus par phases : montre au spectateur que le point de hitbox se resserre.
  out.focus = (tick + 40) % 300 < 90;
  // Secondaire par à-coups réguliers.
  out.secondary = tick % 96 < 8;
  // Une bombe, une seule fois par boucle, à mi-parcours.
  out.bomb = tick === 1500;
  out.aimX = undefined;
  out.aimY = undefined;
  return out;
}
